import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell, CheckCircle2, Mail, Trash2, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listSentEmails, type SentEmail } from '@/services/api/emails';
import { routes } from '@/lib/routes';
import { cn } from '@/lib/cn';
import { playSound } from '@/lib/sounds';
import { useRealtimeTables } from '@/hooks/useRealtimeTables';

interface Props {
  empresaId: string;
}

/**
 * Campanita de notificaciones.
 * Muestra los últimos envíos de email no dismisseados. Estado de "visto"
 * persiste en localStorage (por empresa). El usuario los dismissa:
 *   · Click en uno individual → se abre el envío y queda dismissado.
 *   · Botón "Volá todo" → dismissa todos de una.
 */
export function NotificationBell({ empresaId }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<SentEmail[] | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed(empresaId));
  const ref = useRef<HTMLDivElement>(null);
  const lastTopIdRef = useRef<string | null>(null);

  // Persistir cada cambio en dismissed
  useEffect(() => {
    saveDismissed(empresaId, dismissed);
  }, [dismissed, empresaId]);

  const reload = useCallback(
    async (playIfNew = false) => {
      const res = await listSentEmails({ empresaId, limit: 30 }); // tomamos 30 para tener margen
      if (!res.ok) return;
      const newRows = res.data;
      const newTop = newRows[0]?.id ?? null;
      // Solo suena si el top nuevo NO está dismissado (es realmente una novedad)
      if (
        playIfNew &&
        lastTopIdRef.current !== null &&
        newTop &&
        newTop !== lastTopIdRef.current &&
        !dismissed.has(newTop)
      ) {
        playSound('notify');
      }
      lastTopIdRef.current = newTop;
      setRows(newRows);
    },
    [empresaId, dismissed],
  );

  useEffect(() => {
    void reload(false);
  }, [reload]);

  // Fallback por si el WS se cae — cada 5 minutos. Antes era 2 min, pero
  // como el realtime ya cubre el caso normal (ver useRealtimeTables abajo),
  // este timer es sólo para resincronización oportunista.
  useEffect(() => {
    const t = setInterval(() => void reload(true), 300_000);
    return () => clearInterval(t);
  }, [reload]);

  // Realtime: escuchamos INSERT/UPDATE en sent_emails de la empresa.
  // Al cambio → reload con sonido si hay novedad genuina (la lógica de
  // `dismissed` dentro de reload ya filtra los casos). Hook compartido
  // con LotesTab para consistencia + cleanup.
  useRealtimeTables(
    `sent_emails:empresa:${empresaId}`,
    [{ table: 'sent_emails', filter: `empresa_id=eq.${empresaId}` }],
    () => {
      void reload(true);
    },
    Boolean(empresaId),
  );

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Lista visible: sólo las no dismisseadas, tope 10.
  const visibles = useMemo(
    () => (rows ?? []).filter((r) => !dismissed.has(r.id)).slice(0, 10),
    [rows, dismissed],
  );

  const failCount = visibles.filter((r) =>
    ['failed', 'bounced', 'complained'].includes(r.estado),
  ).length;

  function dismiss(id: string) {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  function dismissAll() {
    if (visibles.length === 0) return;
    setDismissed((prev) => {
      const next = new Set(prev);
      visibles.forEach((r) => next.add(r.id));
      return next;
    });
    playSound('drawer_close');
  }

  function onClickItem(r: SentEmail) {
    dismiss(r.id);
    setOpen(false);
    navigate(routes.empresa.facturacion(empresaId) + '?tab=envios');
  }

  function goVerTodos() {
    setOpen(false);
    navigate(routes.empresa.facturacion(empresaId) + '?tab=envios');
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="Notificaciones"
        className="relative grid h-10 w-10 place-items-center rounded-md text-neutral-ink/70 transition hover:bg-blue-10 hover:text-blue"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) void reload(false);
        }}
      >
        <Bell size={18} />
        {failCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-status-danger px-1 text-[10px] font-bold text-white">
            {failCount > 9 ? '9+' : failCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-[60] mt-2 w-[min(24rem,calc(100vw-1.5rem))] max-w-[24rem] overflow-hidden rounded-md border border-surface-line bg-white shadow-premium">
          <div className="flex items-center justify-between gap-2 border-b border-surface-line px-4 py-2.5">
            <p className="kicker">Últimos envíos</p>
            <div className="flex items-center gap-3">
              {visibles.length > 0 && (
                <button
                  type="button"
                  onClick={dismissAll}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange hover:underline"
                  title="Limpiar todas las notificaciones"
                >
                  <Trash2 size={11} /> Volá todo
                </button>
              )}
              <button
                type="button"
                onClick={goVerTodos}
                className="text-[11px] font-semibold text-blue hover:underline"
              >
                Ver todos →
              </button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {!rows ? (
              <p className="p-6 text-center text-xs text-neutral-muted">Cargando…</p>
            ) : visibles.length === 0 ? (
              <div className="px-6 py-8 text-center">
                <Mail size={22} className="mx-auto mb-2 text-neutral-muted/60" />
                <p className="text-sm font-semibold text-neutral-ink">
                  Todo tranqui por acá.
                </p>
                <p className="mt-1 text-[11px] text-neutral-muted">
                  Cuando haya novedades, te las contamos.
                </p>
              </div>
            ) : (
              visibles.map((r) => {
                const isFail = ['failed', 'bounced', 'complained'].includes(r.estado);
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onClickItem(r)}
                    className={cn(
                      'flex w-full items-start gap-2 border-b border-surface-line/60 px-4 py-2.5 text-left transition last:border-0',
                      'hover:bg-blue-10/40',
                      isFail && 'bg-status-danger/5 hover:bg-status-danger/10',
                    )}
                  >
                    <div className="mt-0.5 shrink-0">
                      {isFail ? (
                        <XCircle size={14} className="text-status-danger" />
                      ) : (
                        <CheckCircle2 size={14} className="text-status-success" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold text-neutral-ink">
                        {r.asunto}
                      </p>
                      <p className="truncate text-[11px] text-neutral-muted">
                        → {r.to_email}
                      </p>
                      {isFail && r.error_msg && (
                        <p className="mt-0.5 line-clamp-2 text-[10px] text-status-danger">
                          {r.error_msg}
                        </p>
                      )}
                    </div>
                    <p className="tabular shrink-0 text-[10px] text-neutral-muted">
                      {relTime(r.created_at)}
                    </p>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Persistencia de dismissed (por empresa) en localStorage
// ═══════════════════════════════════════════════════════════════════════════
function storageKey(empresaId: string): string {
  return `manaxer.notifs.dismissed.${empresaId}`;
}

function loadDismissed(empresaId: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(storageKey(empresaId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    // Cap a 500 ids para que no crezca sin límite.
    return new Set(arr.slice(-500));
  } catch {
    return new Set();
  }
}

function saveDismissed(empresaId: string, set: Set<string>) {
  if (typeof window === 'undefined') return;
  try {
    const arr = Array.from(set).slice(-500);
    window.localStorage.setItem(storageKey(empresaId), JSON.stringify(arr));
  } catch {
    /* noop */
  }
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'ahora';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
  });
}
